import { useSelector } from 'react-redux';
import { Navigate, useLocation } from 'react-router-dom';
import { useWhatsAppData } from '../../hooks/useWhatsAppData';
import { WhatsAppLoading, WhatsAppNetworkError, WhatsAppPermissionDenied } from '../../layouts/WhatsApp/components/ui';

/**
 * Route guard component for WhatsApp routes
 * Checks user permissions and WhatsApp configuration before allowing access
 */
const WhatsAppRouteGuard = ({ children }) => {
  const location = useLocation();
  const { access_token, role } = useSelector((state) => state.auth.user);

  // Check if user has permission to access WhatsApp features
  const hasWhatsAppPermission = () => {
    if (!role) return false;
    const allowedRoles = ['user', 'agent', 'manager', 'admin'];
    return allowedRoles.includes(role);
  };

  // Fetch WhatsApp data to validate configuration
  const { data: waCombinedData, error, isLoading } = useWhatsAppData(
    'get-combined-data',
    access_token?.token,
    {},
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      shouldRetryOnError: false,
    }
  );

  // Show loading state while validating permissions
  if (isLoading) {
    return <WhatsAppLoading />;
  }

  // Check if user is authenticated
  if (!access_token?.token) {
    return <Navigate to="/login" replace />;
  }

  // Check user role permissions first
  if (!hasWhatsAppPermission()) {
    return <WhatsAppPermissionDenied />;
  }

  // Handle network/permission fetch errors
  if (error) {
    // Check if it's a network error vs permission error
    if (error.status === 401 || error.status === 403) {
      return <WhatsAppPermissionDenied />;
    } else {
      return <WhatsAppNetworkError onRetry={() => window.location.reload()} />;
    }
  }

  // For manager routes, check if user is actually a manager
  if (location.pathname.startsWith('/manager/dashboard/whatsapp')) {
    if (role !== 'manager' && role !== 'admin') {
      return <Navigate to="/dashboard" replace />;
    }
  }

  // For regular user routes, ensure user is not trying to access manager routes
  if (location.pathname.startsWith('/dashboard/whatsapp') && role === 'manager') {
    return <Navigate to="/manager/dashboard/whatsapp" replace />;
  }

  // Validate WhatsApp configuration exists
  const firstItem = waCombinedData?.data?.combinedData?.[0];
  const hasWhatsAppConfig = Boolean(
    firstItem?.waba_id &&
    firstItem.phoneNumbers?.data?.length > 0 &&
    firstItem.phoneNumbers?.data[0]?.id
  );

  // For simplified routing, always allow access to main WhatsApp page
  // The WhatsAppNew component will handle the internal navigation logic
  if (location.pathname.endsWith('/whatsapp')) {
    return children;
  }

  // If no WhatsApp config and not on getting-started, sandbox, or main WhatsApp page, redirect
  if (!hasWhatsAppConfig &&
    !location.pathname.includes('/getting-started') &&
    !location.pathname.includes('/sandbox') &&
    !location.pathname.endsWith('/whatsapp')) {
    const redirectPath = location.pathname.startsWith('/manager')
      ? '/manager/dashboard/whatsapp'
      : '/dashboard/whatsapp';
    return <Navigate to={redirectPath} replace />;
  }

  // All checks passed, render the protected content
  return children;
};

export default WhatsAppRouteGuard;